import { Injectable } from '@angular/core';
import { protectSubscription } from '@nagel-utils';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import { RequestService } from '@nagel-services';
import { AutocompleteOptionsRequestParams, FieldOption } from '@nagel-types';
import { AutocompleteRequestFilterCriterias } from 'libs/nagel-types/src/lib/fieldDefinitionOptions';

const DEBOUNCE_TIME = 500;
const DEFAULT_PAGE_SIZE = 10;
const START_INDEX = 0;

@Injectable({
    providedIn: 'root'
})
export class AutocompleteService {
    options$: BehaviorSubject<FieldOption[]> = new BehaviorSubject<FieldOption[]>([]);
    isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    optionsObservable$ = this.options$.asObservable();
    shouldFireRequest$ = new BehaviorSubject<AutocompleteOptionsRequestParams>({url: '', filterCriterias: [], responseMapperFn: (response: any) => []});
    isAwaitingDebounce$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    isPaginating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    pageSize = DEFAULT_PAGE_SIZE;
    startIndex = START_INDEX;

    constructor(private requestService: RequestService) {
        this.shouldFireRequest$
            .pipe(
                tap(() => this.isAwaitingDebounce$.next(true)),
                debounceTime(DEBOUNCE_TIME)
            )
            .subscribe(({url, filterCriterias, responseMapperFn}) => {
                if (url) {
                    this.getAutocompleteOptions(url, filterCriterias, responseMapperFn);
                } else {
                    this.isLoading$.next(false);
                    this.isAwaitingDebounce$.next(false);
                }
            });
    }

    getAutocompleteOptionsRequest = <ResponseType>(url: string, filterCriterias: AutocompleteRequestFilterCriterias[]) => {
        const body = {
            filterCriterias,
            offsetInfo: {
                offset: this.startIndex * this.pageSize,
                limit: this.pageSize
            }
        };

        this.isLoading$.next(true);

        return this.requestService.postRetrieveRequest<Record<string, object>, ResponseType>(url, body);
    };

    getAutocompleteOptions = <ResponseType>(
        url: string,
        filterCriterias: AutocompleteRequestFilterCriterias[],
        responseMapperFn: (response: ResponseType) => FieldOption[]
    ): void => {
        const shouldEmitSubscriptionOnce = true;

        protectSubscription(
            this.getAutocompleteOptionsRequest<ResponseType>(url, filterCriterias),
            (successResponse) => this.onOptionsRetrievalSuccessful<ResponseType>(successResponse, responseMapperFn),
            this.onError,
            shouldEmitSubscriptionOnce
        );
    };

    onError = (error: unknown) => {
        this.isLoading$.next(false);
        console.error(error);
        this.isAwaitingDebounce$.next(false);
        this.isPaginating$.next(false);
    };

    onOptionsRetrievalSuccessful = <ResponseType>(
        response: ResponseType,
        responseMapperFn: (res: ResponseType) => FieldOption[]
    ) => {
        this.isLoading$.next(false);

        const mappedOptions = responseMapperFn(response);
        this.options$.next(mappedOptions);
        this.isAwaitingDebounce$.next(false);
        this.isPaginating$.next(false);
    };
}